/**
 * The intro card — `docs/proposal.md` §6 item 3.
 *
 * It overlaps the cover band by 76px, which is what ties the two together:
 * the band is the company and the card is the person who wrote this.
 *
 * **The message has nothing above it** — no estimator line, no avatar, no
 * "A note from Alex". It is a note, and a note with a byline stapled over it
 * reads as a template. The estimator appears **once** on this page, as the
 * third fact, which is also the answer to "who do I reply to".
 *
 * **There is no "Starts" fact.** Nothing on this page states a start date
 * (§2 rule 11): the office's planning dates are not a promise, and a date in
 * a box beside "Quote date" is exactly how one becomes one.
 */
import type { ProposalView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDocumentDate } from '../../lib/format.js'
import { IconClock, IconFile, IconPerson } from '../../frame/icons.js'

export function ProposalIntro({ view, phone }: { view: ProposalView; phone: boolean }) {
  const estimator = view.estimator
  return (
    <section className={cn('rounded-[18px] border border-line bg-surface shadow-proposal',
      phone ? 'flex flex-col gap-4 px-5 py-6' : 'flex flex-col gap-[22px] px-[34px] pb-7 pt-8')}>
      {view.message && (
        <p className={cn('m-0 text-text-body', phone ? 'text-[15.5px] leading-[1.55]' : 'text-[17.5px] leading-[1.6]')}>
          {view.message}
        </p>
      )}
      {view.message && <div className="h-px bg-line-soft" />}

      <div className={cn('flex', phone ? 'flex-col gap-4' : 'items-stretch gap-6 max-md:flex-col')}>
        <Fact
          icon={<IconFile size={19} />}
          label="Quote date"
          value={view.quoteDate ? formatDocumentDate(view.quoteDate) : '—'}
        />
        {/*
          * Absent rather than "—" when a draft has no expiry yet: the date is
          * computed on the day it is sent (§10.1 card 1), so a third of the
          * row saying nothing is more honest than a dash somebody reads as
          * "this never expires".
          */}
        {view.expiresOn && (
          <Fact
            icon={<IconClock size={19} />}
            label="Valid until"
            value={formatDocumentDate(view.expiresOn)}
          />
        )}
        {estimator && (
          <Fact
            icon={<IconPerson size={19} />}
            label="Prepared by"
            value={estimator.name}
          />
        )}
      </div>
    </section>
  )
}

function Fact({ icon, label, value, sub }: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px]
                       bg-brand-t2 text-brand-tx">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[12px] font-semibold text-text-faint">{label}</span>
        <b className="truncate text-[15px] font-semibold text-text">{value}</b>
        {sub && <span className="truncate text-[12px] text-text-muted">{sub}</span>}
      </span>
    </div>
  )
}
